import { Check } from "lucide-react";

import { cn, formatUsdCurrency } from "@/lib/utils";

interface PricingCardProps {
  name: string;
  description: string;
  price: number;
  priceUnit?: "mo" | "yr";
  billingNote?: string;
  bestFor?: string;
  features: string[];
  cta?: string;
  ctaHref?: string;
  popular?: boolean;
  className?: string;
}

export function PricingCard({
  name,
  description,
  price,
  priceUnit = "mo",
  billingNote,
  bestFor,
  features,
  cta = "Get started",
  ctaHref = "#",
  popular,
  className,
}: PricingCardProps) {
  const formattedPrice = formatUsdCurrency(price);

  return (
    <div
      className={cn(
        "group relative flex flex-col rounded-[2.25rem] p-7 transition-all duration-300",
        popular
          ? "paper-card card-hover-lift border-2 border-primary/20"
          : "paper-card card-hover-lift border border-border/70",
        className,
      )}
      style={
        popular
          ? {
              backgroundImage:
                "radial-gradient(900px circle at 20% 10%, color-mix(in srgb, var(--primary) 12%, transparent), transparent 56%), radial-gradient(900px circle at 80% 0%, color-mix(in srgb, var(--primary) 8%, transparent), transparent 55%)",
            }
          : undefined
      }
    >
      {popular && (
        <span className="stamp absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-[11px] font-semibold text-foreground/70">
          <span className="h-1.5 w-1.5 rounded-full bg-primary/60" aria-hidden="true" />
          Most popular
        </span>
      )}
      <div>
        <h3 className="font-display text-2xl font-extrabold tracking-tight">{name}</h3>
        <p className="mt-1 text-sm text-muted-foreground">{description}</p>
        {bestFor && <p className="mt-1 text-xs font-medium text-primary">{bestFor}</p>}
      </div>
      <div className="mt-4 flex items-baseline gap-1">
        {price === 0 ? (
          <span className="font-display text-5xl font-extrabold tracking-tight">Free</span>
        ) : (
          <>
            <span className="font-display text-5xl font-extrabold tracking-tight">
              {formattedPrice}
            </span>
            <span className="text-muted-foreground">/{priceUnit}</span>
          </>
        )}
      </div>
      {billingNote && <p className="mt-2 text-xs text-muted-foreground">{billingNote}</p>}
      <ul className="mt-6 space-y-3 flex-1">
        {features.map((feature) => (
          <li key={feature} className="flex items-start gap-2 text-sm">
            <Check className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>
      <a
        href={ctaHref}
        className={cn(
          "mt-6 inline-flex w-full items-center justify-center rounded-2xl px-4 py-3 text-sm font-semibold transition-all duration-200",
          popular
            ? "bg-primary text-primary-foreground shadow-md shadow-primary/15 hover:shadow-lg hover:brightness-105"
            : "paper-tab hover:bg-muted/40",
        )}
      >
        {cta}
      </a>
    </div>
  );
}
